import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import Button from 'components/Button/Button';
import PricingPlan from 'components/PricingPlan/PricingPlan';
import Battery from 'components/Battery/Battery';
import Braintree from 'components/Braintree/Braintree';
import Spinner from 'components/Spinner/Spinner';
import ScooterPreview from 'components/ScooterPreview/ScooterPreview';
import Close from 'components/Close/Close';
import Rules from 'components/Rules/Rules';
import ScootersStore from 'stores/Scooters';
import RidesStore from 'stores/Rides';
import AlertStore from 'stores/Alert';
import UserStore from 'stores/User';
import batteryToMiles from 'helpers/battery-to-miles';
import styles from './ScooterShow.module.scss';

const ScooterShow = observer(() => {

  const { id } = useParams();
  const navigate = useNavigate();
  const braintree = useRef(null);
  const [ unlocking, setUnlocking ] = useState(false);
  const [ selectedPlan, setSelectedPlan ] = useState(null);
  const [ tutorialVisible, setTutorialVisible ] = useState(false);

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    if (ScootersStore.selected) {
      setSelectedPlan(ScootersStore.selected?.pricingPlans?.[0]?.id);
    }
  }, [ ScootersStore.selected ]);

  async function fetch() {
    try {
      await ScootersStore.fetch(id);
    } catch (err) {
      AlertStore.add('Scooter not found');
      navigate('/ride/create');
    }
  }

  async function unlock() {
    try {
      setUnlocking(true);
      const { success } = await UserStore.hasCard();

      if (!success) {
        await braintree.current.init();
      }
      await RidesStore.start(scooter?.id, selectedPlan);
      setUnlocking(false);
      navigate('/ride');
    } catch (err) {
      AlertStore.add(err.message);
      setUnlocking(false);
    }
  }

  function start() {
    if (!UserStore.id) {
      AlertStore.add('Please login to start the ride');
      sessionStorage.setItem('redirect', `/scooters/${id}`);
      return navigate('/auth');
    }

    const state = !!window.localStorage?.getItem('tutorial_viewved');
    if (state) {
      return unlock();
    }
    
    setTutorialVisible(true);
  }

  function tutorialComplete() {
    setTutorialVisible(false);
    unlock();
  }

  const scooter = ScootersStore.selected;

  return (
    <div className={styles.container}>
      <Close to="/ride/create" />
      <div className={styles.header}>
        <div className={styles.id}>
          #{scooter?.id ?? id}
        </div>
        <div className={styles.battery}>
          <div className={styles.miles}>
            {batteryToMiles(scooter?.battery ?? 0)} MI
          </div>
          <div className={styles.range}>
            <Battery value={scooter?.battery ?? 0} />
            Range
          </div>
        </div>

        <ScooterPreview model={scooter?.vehicleModel} />
      </div>

      <div className={styles.plans}>
        <div className={styles.title}>
          Select Rate
        </div>

        {scooter?.pricingPlans?.map(plan =>
          <PricingPlan 
            plan={plan} 
            onClick={event => setSelectedPlan(plan.id)} 
            selected={selectedPlan === plan.id} 
            key={plan.id} 
          />
        )}
      </div>
      
      <div className={styles.footer}>
        {unlocking ? 
          <Spinner />
        :
          <Button onClick={start}>
            Unlock #{scooter?.id ?? id}
          </Button>
        }
      </div>
      
      <Braintree ref={braintree} />
      <Rules 
        visible={tutorialVisible} 
        onComplete={tutorialComplete}
      />
    </div>
  );
});

export default ScooterShow;
